import React, { useState, useEffect } from 'react';
import { Gamepad2, Star, Search, Filter, Download, Crown, Zap, TrendingUp, Trophy, X, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../i18n';
import { XlevenLogo } from '../Common/XlevenLogo';
import { ConfirmModal } from '../Common/ConfirmModal';
import { gameService } from '../../services/gameService';
import { StoreGame } from '../../types';
import SEO from '../Common/SEO';

interface GameRanking {
    rank: number;
    nickname: string;
    score: number;
}

export const GameStoreView: React.FC = () => {
    const { lang } = useLanguage();
    const { user } = useAuth();

    const [games, setGames] = useState<StoreGame[]>([]);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingGameId, setLoadingGameId] = useState<string | null>(null);
    const [isTotoConfirmOpen, setIsTotoConfirmOpen] = useState(false);
    const [isRankingModalOpen, setIsRankingModalOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [selectedGameForRanking, setSelectedGameForRanking] = useState<string | null>(null);
    const [rankings, setRankings] = useState<GameRanking[]>([]);
    const [isRankingLoading, setIsRankingLoading] = useState(false);

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const data = await gameService.getStoreGames();
                // Filter out hidden games
                setGames((data || []).filter(g => !g.is_hidden));
            } catch (error) {
                console.error('Failed to fetch games:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchGames();
    }, []);

    const openRanking = async (gameId: string) => {
        setSelectedGameForRanking(gameId);
        setIsRankingLoading(true);
        
        try {
            const data = await gameService.getTopScores(gameId);
            setRankings(data);
            
            // 모달을 보이지 않는 상태(opacity-0)로 먼저 렌더링하여 높이를 계산하게 합니다.
            setIsRankingModalOpen(true);
            
            // 렌더링 완료 후 레이아웃이 잡힐 시간을 충분히 준 뒤 애니메이션을 시작합니다.
            setTimeout(() => {
                setIsAnimating(true);
                setIsRankingLoading(false);
            }, 100);
            
        } catch (error) {
            console.error('Failed to fetch rankings:', error);
            setIsRankingLoading(false);
        }
    };

    const closeRanking = () => {
        setIsAnimating(false);
        setTimeout(() => {
            setIsRankingModalOpen(false);
            setSelectedGameForRanking(null);
            setRankings([]);
        }, 500);
    };

    const handlePlayGame = (game: StoreGame) => {
        if (!game.link) return;

        window.navigateTo(game.link!);
    };

    const handleLoginRedirect = () => {
        setIsLoginModalOpen(false);
        if (window.navigateTo) window.navigateTo('/login');
    };

    return (
        <div className="min-h-screen bg-white">
            <SEO title={t(lang, 'gameStoreTitle')} />
            <header className="sticky top-0 md:hidden z-30 px-4 h-14 bg-white/95 backdrop-blur-lg border-b border-gray-100">
                <div className="max-w-2xl mx-auto flex items-center justify-between h-full">
                    <div className="flex items-center gap-2">
                        <XlevenLogo />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
                        <Gamepad2 size={20} />
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-4 pb-40">
                <div className="space-y-0">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-3">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{t(lang, 'loading')}</p>
                        </div>
                    ) : games.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <div className="w-full bg-slate-50 rounded-[32px] p-10 flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-slate-200 mb-4 shadow-sm">
                                    <Gamepad2 size={32} />
                                </div>
                                <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-2">{t(lang, 'newGamePreparing')}</h3>
                                <p className="text-xs font-medium text-slate-400 leading-relaxed max-w-[240px]">
                                    {t(lang, 'newGamePreparingDesc')}
                                </p>
                            </div>
                        </div>
                    ) : (
                        games.map(game => (
                            <div key={game.id} className="flex items-center gap-4 py-4 border-b border-gray-100 last:border-0 group">
                                <div className={`w-16 h-16 bg-gradient-to-br from-white to-gray-50 border border-gray-100 rounded-[18px] flex items-center justify-center text-3xl shadow-lg shadow-gray-200/50 flex-shrink-0 transition-transform group-active:scale-95`}>
                                    {game.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <h3 className="text-[17px] font-bold text-gray-900 truncate uppercase tracking-tight">
                                            {game.title}
                                        </h3>
                                        {(game as any).isPremium && <Crown size={12} className="text-amber-400 fill-amber-400" />}
                                    </div>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{game.category}</p>
                                </div>
                                <div className="flex flex-col items-center gap-1.5">
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={() => openRanking(game.id)}
                                            className="px-4 py-1.5 bg-gray-100 md:hover:bg-gray-200 text-gray-600 rounded-full text-xs font-bold transition-colors active:scale-95 flex-shrink-0"
                                        >
                                            {t(lang, 'ranking')}
                                        </button>
                                        <button 
                                            onClick={() => handlePlayGame(game)}
                                            disabled={loadingGameId !== null}
                                            className={`relative px-4 py-1.5 rounded-full text-xs font-bold transition-all active:scale-95 flex-shrink-0 min-w-[70px] flex items-center justify-center overflow-hidden ${
                                                loadingGameId === game.id 
                                                ? 'bg-blue-50 text-blue-600' 
                                                : 'bg-blue-600 md:hover:bg-blue-700 text-white'
                                            }`}
                                        >
                                            {loadingGameId === game.id ? (
                                                <div className="flex flex-col items-center">
                                                    <div className="absolute inset-0 bg-blue-100/50 origin-left animate-[loading_1s_ease-in-out]" />
                                                    <span className="relative z-10 text-[10px]">{t(lang, 'preparing')}</span>
                                                </div>
                                            ) : (
                                                t(lang, 'play')
                                            )}
                                        </button>
                                    </div>
                                    {game.isNew && <span className="text-[9px] text-blue-500 font-black uppercase tracking-widest">New</span>}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </main>

            {loadingGameId?.startsWith('OVERLAY_') && (
                <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center overflow-hidden animate-in fade-in duration-500">
                    <div 
                        className="absolute inset-0 opacity-40 bg-cover bg-center animate-[zoom-out_5s_ease-out_forwards]" 
                        style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1522778119026-d647f0596c20?q=80&w=2070&auto=format&fit=crop")' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                    <div className="relative z-10 flex flex-col items-center">
                        <div className="relative w-48 h-48 mb-12 flex items-center justify-center">
                            <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
                            <div className="absolute inset-0 animate-[ping_3s_linear_infinite]" />
                            <div className="relative p-10 animate-[float_4s_easeInOut_infinite]">
                                <svg viewBox="0 0 24 24" className="w-24 h-24 text-white fill-none stroke-current stroke-[1.2]">
                                    <rect x="2" y="4" width="20" height="16" rx="2" />
                                    <circle cx="12" cy="12" r="3" />
                                    <path d="M2 12h3M19 12h3M12 4v2M12 18v2" />
                                    <path d="M7 8v8M17 8v8" />
                                    <line x1="12" y1="4" x2="12" y2="20" className="opacity-20" />
                                </svg>
                                <div className="absolute top-4 left-4 w-4 h-4 border-t-2 border-l-2 border-blue-400" />
                                <div className="absolute top-4 right-4 w-4 h-4 border-t-2 border-r-2 border-blue-400" />
                                <div className="absolute bottom-4 left-4 w-4 h-4 border-b-2 border-l-2 border-blue-400" />
                                <div className="absolute bottom-4 right-4 w-4 h-4 border-b-2 border-r-2 border-blue-400" />
                            </div>
                        </div>
                        <div className="w-80 px-4">
                            <div className="flex justify-between items-end mb-3">
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-black text-blue-400 tracking-[0.4em] uppercase mb-1">Status</span>
                                    <span className="text-sm font-bold text-white tracking-widest uppercase animate-pulse">Initializing Arena...</span>
                                </div>
                                <span className="text-2xl font-black text-white font-orbitron italic tracking-tighter">100%</span>
                            </div>
                            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-blue-600 via-cyan-300 to-blue-600 rounded-full animate-[loading_5s_linear_forwards]" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Login Required Modal */}
            <ConfirmModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onConfirm={handleLoginRedirect}
                title={t(lang, 'loginRequired')}
                description={t(lang, 'loginParticipate')}
                confirmLabel={t(lang, 'signInLabel')}
                cancelLabel={t(lang, 'cancel')}
            />

            {/* Ranking Modal */}
            {isRankingModalOpen && (
                <div className={`fixed inset-0 z-[300] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={closeRanking}></div>
                    <div className={`relative bg-white w-full sm:max-w-md rounded-t-[40px] sm:rounded-[32px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-100">
                                    <Trophy size={16} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900 leading-tight">{t(lang, 'gameRanking')}</h2>
                                </div>
                            </div>
                            <button 
                                onClick={closeRanking}
                                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 md:hover:text-gray-900 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4 max-h-[400px] overflow-y-auto no-scrollbar">
                            {isRankingLoading ? (
                                <div className="flex flex-col items-center justify-center py-20 gap-3">
                                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                                    <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{t(lang, 'fetchingRankings')}</p>
                                </div>
                            ) : rankings.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 gap-3">
                                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
                                        <Trophy className="w-8 h-8 text-slate-200" />
                                    </div>
                                    <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{t(lang, 'noRankingsYet')}</p>
                                </div>
                            ) : rankings.map((entry) => (
                                <div key={entry.rank} className="flex items-center gap-4 group">
                                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-sm font-black transition-transform md:group-hover:scale-110 ${
                                        entry.rank === 1 ? 'bg-yellow-100 text-yellow-600' :
                                        entry.rank === 2 ? 'bg-slate-100 text-slate-500' :
                                        entry.rank === 3 ? 'bg-orange-100 text-orange-600' :
                                        'bg-gray-50 text-gray-300'
                                    }`}>
                                        {entry.rank}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-900 truncate">{entry.nickname}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-black text-blue-600 tracking-tight">{entry.score.toLocaleString()}</p>
                                        <p className="text-[9px] font-bold text-gray-400 uppercase">Points</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                       
                    </div>
                </div>
            )}
        </div>
    );
};
